import React, { useState, useContext, useCallback } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  Input,
  Text,
  VStack,
  Center,
} from '@chakra-ui/react';
import Map from 'google-map-react';
import useCartStore from '../../../../store/cartStore';
import { ImLocation2 } from 'react-icons/im';
import { CartContext } from '../../../../context/cart';
import useLabsStore from '../../../../store/labsStore';
import axios from 'axios';

// Red Marker Component (Fixed at center)
const CenterMarker = () => (
  <Center
    position="absolute"
    top="50%"
    left="50%"
    transform="translate(-50%, -100%)"
    pointerEvents="none"
    zIndex={1}
  >
    <VStack spacing={0}>
      <Box
        bg="white"
        p={2}
        borderRadius="full"
        boxShadow="xl"
        mb={-1}
        border="2px solid red"
      >
        <ImLocation2 color="red" size="24px" />
      </Box>
      <Box w="2px" h="20px" bg="red.500" shadow="lg" />
    </VStack>
  </Center>
);

export const LocationDrawer = props => {
  const [map, setMap] = useState(null);
  const [mapsLib, setMapsLib] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [center, setCenter] = useState({ lat: 28.6139, lng: 77.2090 });
  const [inputValue, setInputValue] = useState('');
  const { isLocationOpen, setIsLocationOpen, setNearbyLabs } = useCartStore();
  const { setLocation, activeStep, setActiveStep } = useContext(CartContext);
  const { setLabData } = useLabsStore();

  // Reverse Geocoding Logic
  const fetchAddressFromCoords = useCallback(async (coords) => {
    if (!mapsLib) return;
    const geocoder = new mapsLib.Geocoder();
    geocoder.geocode({ location: coords }, async (results, status) => {
      if (status === "OK" && results[0]) {
        const formattedAddress = results[0].formatted_address;
        setInputValue(formattedAddress);
      } else {
        console.error("Google Geocode failed:", status);
        // Secondary Fallback: Use OpenStreetMap (Nominatim) if Google fails
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&zoom=18&addressdetails=1`
          );
          const data = await response.json();
          if (data && data.display_name) {
            setInputValue(data.display_name);
          } else {
            setInputValue(`${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`);
          }
        } catch (error) {
          console.error("OSM Geocode failed:", error);
          setInputValue(`${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}`);
        }
      }
    });
  }, [mapsLib]);

  const handleMapChange = ({ center }) => {
    setCenter(center);
    fetchAddressFromCoords(center);
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) return;
    setLoading(true);
    setInputValue("Fetching current address...");
    
    navigator.geolocation.getCurrentPosition(
      pos => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(coords);
        if (map) map.panTo(coords);
        
        // Try to get address
        fetchAddressFromCoords(coords);
        setLoading(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        setLoading(false);
        setInputValue("Location access denied");
      },
      { enableHighAccuracy: true }
    );
  };

  const handleAutocomplete = (val) => {
    setInputValue(val);
    if (!mapsLib || val.length < 3) return;
    const service = new mapsLib.places.AutocompleteService();
    service.getPlacePredictions({ input: val, componentRestrictions: { country: 'in' } }, (preds) => {
      setSuggestions(preds || []);
    });
  };

  const handleSelectSuggestion = (p) => {
    if (!mapsLib) return;
    const service = new mapsLib.places.PlacesService(map);
    service.getDetails({ placeId: p.place_id }, (place) => {
      if (place.geometry) {
        const coords = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
        setCenter(coords);
        if (map) map.panTo(coords);
        setSuggestions([]);
        setInputValue(p.description);
      }
    });
  };

  const calculateDistance = (loc1, loc2) => {
    const R = 6371e3;
    const f1 = loc1.lat * (Math.PI / 180);
    const f2 = loc2.lat * (Math.PI / 180);
    const df = (loc2.lat - loc1.lat) * (Math.PI / 180);
    const dl = (loc2.lng - loc1.lng) * (Math.PI / 180);
    const a = Math.sin(df/2) * Math.sin(df/2) + Math.cos(f1) * Math.cos(f2) * Math.sin(dl/2) * Math.sin(dl/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      setLocation(inputValue || "Selected Location");
      const res = await axios.get(`${process.env.REACT_APP_SERVER}/getLabs`);
      const allLabs = res?.data?.labs?.filter(lab => lab?.isApproved) || [];
      
      const nearby = allLabs.map(lab => {
          if (!lab.labAddress?.lat || !lab.labAddress?.lng) return { ...lab, distance: Infinity };
          const dist = calculateDistance(center, { lat: lab.labAddress.lat, lng: lab.labAddress.lng });
          return { ...lab, distance: dist };
      }).filter(lab => lab.distance < 50000) // Increase radius to 50km
        .sort((a, b) => a.distance - b.distance);

      setNearbyLabs(nearby);
      if (nearby.length > 0) {
          setLabData(nearby[0]);
      } else if (allLabs.length > 0) {
          // If still no labs found within 50km, show top 3 closest labs anyway
          const sortedAll = allLabs.map(lab => {
            if (!lab.labAddress?.lat || !lab.labAddress?.lng) return { ...lab, distance: Infinity };
            return { ...lab, distance: calculateDistance(center, { lat: lab.labAddress.lat, lng: lab.labAddress.lng }) };
          }).sort((a, b) => a.distance - b.distance);
          
          setNearbyLabs(sortedAll.slice(0, 5));
          setLabData(sortedAll[0]);
      }
      setIsLocationOpen(false);
      setActiveStep(activeStep + 1);
    } catch (e) {
      console.error(e);
      // Fallback: Proceed anyway
      setIsLocationOpen(false);
      setActiveStep(activeStep + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer placement="right" size={['full', 'md']} onClose={() => setIsLocationOpen(false)} isOpen={isLocationOpen}>
      <DrawerOverlay backdropFilter="blur(10px)" bg="blackAlpha.300" />
      <DrawerContent borderLeftRadius={['0', '3xl']} boxShadow="2xl">
        <DrawerHeader borderBottomWidth="0px" pt={8} px={6}>
          <VStack align="start" spacing={1}>
            <Text fontSize="2xl" fontWeight="800" color="teal.800" lineHeight="1.2">
              Where should we collect <br/> your sample?
            </Text>
            <Text fontSize="sm" fontWeight="medium" color="gray.500">
              Set your location to find the nearest Dlabs facility
            </Text>
          </VStack>
        </DrawerHeader>
        <DrawerCloseButton top={8} right={6} size="lg" borderRadius="full" />
        
        <DrawerBody display="flex" flexDirection="column" gap={6} pb={8} px={6}>
          {/* Search Area */}
          <VStack w="100%" spacing={3} position="relative" zIndex={20}>
            <Box w="100%" position="relative">
              <Input
                placeholder="Search Building, Street, or Pincode..."
                value={inputValue}
                onChange={(e) => handleAutocomplete(e.target.value)}
                bg="gray.50"
                border="2px solid"
                borderColor="gray.100"
                h="60px"
                borderRadius="2xl"
                fontSize="md"
                fontWeight="500"
                _focus={{ borderColor: 'teal.500', bg: 'white', boxShadow: 'xl' }}
                boxShadow="inner"
                pr="60px"
              />
              <Center position="absolute" right="5px" top="5px" h="50px" w="50px">
                <Box color="teal.500" p={2}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                </Box>
              </Center>

              {suggestions.length > 0 && (
                <VStack 
                  position="absolute" 
                  top="70px" 
                  left="0" 
                  right="0" 
                  bg="white" 
                  zIndex={30} 
                  boxShadow="2xl" 
                  borderRadius="2xl" 
                  overflow="hidden" 
                  border="1px solid"
                  borderColor="gray.100"
                  p={2}
                >
                  {suggestions.map(p => (
                    <HStack key={p.place_id} p={4} w="100%" cursor="pointer" _hover={{ bg: 'teal.50' }} onClick={() => handleSelectSuggestion(p)} borderRadius="xl" spacing={4}>
                      <Center w="36px" h="36px" bg="teal.50" borderRadius="full">
                        <ImLocation2 color="#0C727E" />
                      </Center>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="bold" color="gray.700" noOfLines={1}>{p.structured_formatting?.main_text || p.description.split(',')[0]}</Text>
                        <Text fontSize="xs" color="gray.500" noOfLines={1}>{p.structured_formatting?.secondary_text || p.description}</Text>
                      </VStack>
                    </HStack>
                  ))}
                </VStack>
              )}
            </Box>

            {/* Explicit Current Location Button */}
            <Button
              variant="outline"
              colorScheme="teal"
              w="100%"
              h="50px"
              borderRadius="xl"
              leftIcon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>}
              onClick={handleGetCurrentLocation}
              isLoading={loading}
              fontSize="sm"
              fontWeight="bold"
              bg="teal.50"
              border="1px dashed"
              borderColor="teal.300"
              _hover={{ bg: 'teal.100' }}
            >
              Use My Current Location
            </Button>
          </VStack>

          {/* Map Section */}
          <Box 
            flex={1} 
            position="relative" 
            borderRadius="3xl" 
            overflow="hidden" 
            boxShadow="2xl" 
            border="6px solid" 
            borderColor="white"
            h="100%"
            minH="300px"
          >
            <Map
              bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '', libraries: ['places'] }}
              center={center}
              defaultZoom={15}
              onChange={handleMapChange}
              onGoogleApiLoaded={({ map, maps }) => {
                setMap(map);
                setMapsLib(maps);
              }}
              yesIWantToUseGoogleMapApiInternals
              options={{ 
                disableDefaultUI: true, 
                zoomControl: true,
                styles: [
                  { "featureType": "poi", "stylers": [{ "visibility": "off" }] }
                ]
              }}
            />
            <CenterMarker />
            
            {/* Address Overlay */}
            <Box 
              position="absolute" 
              bottom={4} 
              left={4} 
              right={4} 
              bg="whiteAlpha.900" 
              backdropFilter="blur(8px)" 
              p={4} 
              borderRadius="2xl" 
              boxShadow="xl"
              border="1px solid"
              borderColor="white"
            >
              <HStack spacing={3}>
                <Center w="40px" h="40px" bg="teal.500" borderRadius="xl">
                   <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path></svg>
                </Center>
                <VStack align="start" spacing={0} overflow="hidden">
                  <Text fontSize="xs" fontWeight="bold" color="teal.600" textTransform="uppercase" letterSpacing="wider">Collection Point</Text>
                  <Text fontSize="sm" fontWeight="bold" color="gray.800" noOfLines={1}>
                    {inputValue || "Locating..."}
                  </Text>
                </VStack>
              </HStack>
            </Box>
          </Box>

          {/* Action Footer */}
          <VStack w="100%" spacing={4}>
            <Button
              w="100%"
              h="70px"
              bg="#0C727E"
              color="white"
              _hover={{ bg: '#095a63', transform: 'translateY(-2px)', boxShadow: '2xl' }}
              _active={{ transform: 'scale(0.98)' }}
              onClick={handleConfirm}
              isLoading={loading}
              isDisabled={!inputValue || inputValue === 'Fetching current address...'}
              borderRadius="2xl"
              fontSize="xl"
              fontWeight="bold"
              boxShadow="0 10px 20px -5px rgba(12, 114, 126, 0.4)"
            >
              Confirm & Find Labs
            </Button>
            <HStack spacing={2} opacity={0.6}>
               <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
              <Text fontSize="xs" fontWeight="medium" color="gray.600">
                Move the map marker to your exact doorstep
              </Text>
            </HStack>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
